import { Check, Close } from "@mui/icons-material";
import { Box, CircularProgress, colors, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { serviceModulesApi } from "../../../api/serviceModulesApi";
import { CalendarEventSurveyAnswer } from "../../../types/serviceModuleTypes";
import { SurveyEventAnswer } from "../../../types/actionModule/subtypes/surveyAppointmentModels";
import { useTranslation } from "react-i18next";
import { monthCalendarClasses } from "@mui/x-date-pickers";

interface Props {
    serviceModuleId: number;
    eventId: number;
}

export default function CalendarEventSurveyResponses(props: Props) {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [surveyResponses, setSurveyResponses] = useState<CalendarEventSurveyAnswer[] | undefined>(undefined);

    useEffect(() => {
        setIsLoading(true);
        const fetchResponses = async () => {
            const response = await serviceModulesApi.getCalendarEventSurveyResponses(
                props.serviceModuleId,
                props.eventId
            );
            if (response.success() && response.result) {
                setSurveyResponses(response.result);
            }
            setIsLoading(false);
        };
        fetchResponses();
    }, []);

    return <Stack>
        <Typography variant="subtitle2" sx={{ mt: 3 }}>{t("serviceModulesPage.calendarPage.eventSurveyResponsesTitle")}</Typography>
        <Box>
            {
                isLoading && <CircularProgress />
            }
            {
                surveyResponses && surveyResponses.length === 0 && <Typography>
                    {t("serviceModulesPage.calendarPage.eventSurveyResponsesEmpty")}
                </Typography>
            }
            {
                surveyResponses && <Stack direction="column" gap={1}>
                    {
                        surveyResponses.map(r => <SurveyResponse answer={r} />)
                    }
                </Stack>
            }
        </Box>
    </Stack>
}

interface SurveyResponseProps {
    answer: CalendarEventSurveyAnswer;
}

function SurveyResponse(props: SurveyResponseProps) {
    const { t } = useTranslation();

    const color = props.answer.answer === SurveyEventAnswer.Accepted
        ? colors.green[800]
        : colors.red[800];

    const selectionTimestamp = new Date(props.answer.selectionTimestamp + "Z");

    const answerText = props.answer.answer === SurveyEventAnswer.Accepted
        ? t("serviceModulesPage.calendarPage.eventSurveyAnswerAccepted", {
            date: selectionTimestamp, formatParams: {
                date: {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                }
            }
        })
        : t("serviceModulesPage.calendarPage.eventSurveyAnswerDeclined", {
            date: selectionTimestamp, formatParams: {
                date: {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                }
            }
        });

    return <Stack direction="row" alignItems="center" gap={2}>
        {props.answer.answer === SurveyEventAnswer.Accepted && <Check htmlColor={color} />}
        {props.answer.answer === SurveyEventAnswer.Declined && <Close htmlColor={color} />}

        <Stack direction="column">
            <Typography variant="body1">{props.answer.employee.firstName} {props.answer.employee.lastName}</Typography>
            <Typography variant="caption" color={color}>{answerText}</Typography>
        </Stack>
    </Stack>;
}